import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Layers, ExternalLink, Github, Search } from 'lucide-react'
import { Link } from 'react-router-dom'
import { assetUrl, sectionApi, portfolioApi } from '../services/api'

type PortfolioItem = {
  id: string
  title: string
  category: string
  image_url: string | null
  description: string
  tags: string[]
  client: string
  project_date: string
  live_demo_url: string | null
  github_url: string | null
  gallery: string[]
  slug: string
  is_active: boolean
}

export const ProjectsPage: React.FC = () => {
  const [filter, setFilter] = useState("All")
  const [searchTerm, setSearchTerm] = useState("")
  const [portfolioItems, setPortfolioItems] = useState<PortfolioItem[]>([])
  const [sectionHeading, setSectionHeading] = useState<string>('All Projects')
  const [portfolioListHeading, setPortfolioListHeading] = useState<string>('Portfolio Projects')
  const [portfolioListDescription, setPortfolioListDescription] = useState<string>('Recent work and case studies')
  const [categories, setCategories] = useState<string[]>(["All"])
  const [loading, setLoading] = useState(true)
  
  const filteredPortfolio = portfolioItems.filter(p => 
    filter === "All" || p.category === filter
  ).filter(p => 
    searchTerm === "" || p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  )
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        
        // Fetch portfolio items
        const portfolioRes = await portfolioApi.getAll()
        let fetchedPortfolio: PortfolioItem[] = []
        if (portfolioRes?.success && Array.isArray(portfolioRes.data)) {
          fetchedPortfolio = portfolioRes.data.map((item: any) => ({
            id: String(item.id),
            title: item.title || '',
            category: item.category || 'Portfolio',
            image_url: item.image_url || null,
            description: item.description || '',
            tags: Array.isArray(item.tags) ? item.tags : [],
            client: item.client || '',
            project_date: item.project_date || '',
            live_demo_url: item.live_demo_url || null,
            github_url: item.github_url || null,
            gallery: Array.isArray(item.gallery) ? item.gallery : [],
            slug: item.slug || '',
            is_active: item.is_active ?? true
          }))
          
          // Extract categories from portfolio items
          const portfolioCategories = [...new Set(fetchedPortfolio.map(p => p.category))]
          const allCategories = ["All", ...portfolioCategories]
          setCategories(allCategories)
        }
        
        setPortfolioItems(fetchedPortfolio)
        
      } catch (error) {
        console.error('Failed to fetch data:', error)
      } finally {
        setLoading(false)
      }
    }
    
    const fetchHeading = async () => {
      try {
        const h = await sectionApi.get('portfolio')
        if (h?.success && h.data) {
          setSectionHeading(String(h.data.heading || 'All Projects'))
          setPortfolioListHeading(String(h.data.portfolio_page_heading || 'Portfolio Projects'))
          setPortfolioListDescription(String(h.data.portfolio_page_description || 'Recent work and case studies'))
        }
      } catch { void 0 }
    }
    
    fetchData()
    fetchHeading()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full"
        />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-transparent" />
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60vmin] h-[60vmin] bg-amber-500/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 px-6 pt-32 pb-12 md:px-12 md:pt-36 lg:px-24"
      >
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <div className="text-center">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-4xl md:text-6xl font-black italic bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent mb-6"
              >
                {sectionHeading}
              </motion.h1>
            </div>
          </div>

          {/* Category Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-8"
          >
            <div className="flex flex-wrap gap-2 justify-center">
              {categories.map((category) => (
                <motion.button
                  key={category}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: categories.indexOf(category) * 0.05 }}
                  onClick={() => setFilter(category)}
                  className={`px-4 py-2 rounded-full font-medium transition-all ${
                    filter === category
                      ? 'bg-amber-500 text-white'
                      : 'bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10'
                  }`}
                >
                  {category}
                </motion.button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.header>

      {/* Portfolio Items Grid */}
      {filteredPortfolio.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="relative px-6 py-12 md:px-12 lg:px-24"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="mb-12 text-center"
            >
              <h2 className="text-4xl md:text-5xl font-black bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent mb-4">
                {portfolioListHeading}
              </h2>
              <p className="text-gray-400 text-lg">{portfolioListDescription}</p>
            </motion.div>

            <div className="grid gap-8 md:gap-12 grid-cols-1 max-w-5xl mx-auto px-4 md:px-0">
              <AnimatePresence mode="popLayout">
                {filteredPortfolio.map((item, index) => (
                  <motion.div
                    key={item.id}
                    layout
                    initial={{ opacity: 0, y: 50, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -50, scale: 0.9 }}
                    transition={{ 
                      duration: 0.5, 
                      delay: index * 0.1,
                      type: "spring",
                      stiffness: 100
                    }}
                    whileHover={{ 
                      y: -10,
                      scale: 1.02
                    }}
                    className="group relative"
                  >
                    <Link
                      to={`/portfolio/${item.slug}`}
                      className="block relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 hover:border-amber-500/50 transition-all duration-500 shadow-2xl shadow-black/50"
                    >
                      {/* Glow Effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-amber-500/0 via-amber-500/0 to-amber-500/0 group-hover:from-amber-500/10 group-hover:via-amber-500/5 group-hover:to-amber-500/0 transition-all duration-500" />
                      
                      {/* Featured Image */}
                      <div className="aspect-[16/9] md:aspect-[21/9] relative overflow-hidden min-h-[280px] md:min-h-auto">
                        {item.image_url || (Array.isArray(item.gallery) && item.gallery.length > 0) ? (
                          <motion.img
                            src={assetUrl(item.image_url || item.gallery[0])}
                            alt={item.title}
                            className="w-full h-full object-cover"
                            transition={{ duration: 0.5 }}
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center">
                            <motion.div
                              animate={{ 
                                rotate: [0, 360],
                                scale: [1, 1.1, 1]
                              }}
                              transition={{ 
                                duration: 3,
                                repeat: Infinity,
                                ease: "easeInOut"
                              }}
                              className="w-16 h-16 md:w-24 md:h-24 bg-amber-500/20 rounded-full flex items-center justify-center"
                            >
                              <Layers size={32} className="text-amber-400 md:size={48}" />
                            </motion.div>
                          </div>
                        )}
                        
                        {/* Gradient Overlay - Always visible */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent opacity-90" />
                        
                        {/* Content - Always visible */}
                        <div className="absolute bottom-0 left-0 right-0 p-5 md:p-8 lg:p-10">
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 + 0.3 }}
                          >
                            <motion.span
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ delay: index * 0.1 + 0.4 }}
                              whileHover={{ scale: 1.05 }}
                              className="inline-block px-3 py-1.5 md:px-5 md:py-2.5 bg-amber-500/20 border border-amber-500/50 rounded-full text-amber-400 text-xs md:text-sm font-bold uppercase tracking-wider mb-3 md:mb-5 backdrop-blur-md"
                            >
                              {item.category}
                            </motion.span>
                            <h3 className="text-white text-xl md:text-2xl lg:text-3xl xl:text-4xl font-black mb-2 md:mb-4 leading-tight group-hover:text-amber-400 transition-colors">
                              {item.title}
                            </h3>
                            <p className="text-gray-300 text-sm md:text-base lg:text-lg line-clamp-2 mb-4 md:mb-6">
                              {item.description}
                            </p>
                            
                            {/* Action Links */}
                            <div className="flex items-center gap-2 md:gap-4 flex-wrap">
                              {item.live_demo_url && (
                                <motion.a
                                  href={item.live_demo_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  className="inline-flex items-center gap-2 px-4 py-2 md:px-6 md:py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-sm md:text-base font-bold rounded-xl hover:from-amber-400 hover:to-amber-500 transition-all shadow-lg shadow-amber-500/25"
                                >
                                  <ExternalLink size={14} className="md:size={18}" />
                                  <span className="text-sm md:text-base">Live Demo</span>
                                </motion.a>
                              )}
                              {item.github_url && (
                                <motion.a
                                  href={item.github_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  className="inline-flex items-center gap-2 px-4 py-2 md:px-6 md:py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl text-white text-sm md:text-base font-bold hover:bg-white/20 hover:border-amber-500/50 transition-all"
                                >
                                  <Github size={14} className="md:size={18}" />
                                  <span className="text-sm md:text-base">Code</span>
                                </motion.a>
                              )}
                            </div>
                          </motion.div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        </motion.section>
      )}

      {/* No Results */}
      {filteredPortfolio.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center py-32 px-6"
        >
          <motion.div
            animate={{ 
              scale: [1, 1.1, 1],
              rotate: [0, 5, -5, 0]
            }}
            transition={{ 
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="w-32 h-32 bg-gradient-to-br from-amber-500/20 to-amber-600/20 rounded-full flex items-center justify-center mx-auto mb-8 backdrop-blur-xl border border-amber-500/30"
          >
            <Search size={48} className="text-amber-400" />
          </motion.div>
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-3xl md:text-4xl font-black text-white mb-4"
          >
            No Portfolio Items Found
          </motion.h3>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-gray-400 text-lg mb-8 max-w-md mx-auto"
          >
            Try adjusting your filters or check back later for new projects
          </motion.p>
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setSearchTerm("")
              setFilter("All")
            }}
            className="px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-bold rounded-2xl hover:from-amber-400 hover:to-amber-500 transition-all shadow-lg shadow-amber-500/25"
          >
            Clear All Filters
          </motion.button>
        </motion.div>
      )}
    </div>
  )
}

            
